import { Table, Card } from "react-bootstrap";
import { GenericToggle } from "@components/FormsV2/GenericToggle";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import useNotificationChannelOverrides from "@common/hooks/useNotificationChannelOverrides";
import "./styles.css";
import {
  useV2ListNotificationTypesQuery,
  useV2ListNotificationPreferencesQuery,
  useV2CreateNotificationPreferenceMutation,
  useV2UpdateNotificationPreferenceMutation,
} from "@generated";

const UserNotifications = (): JSX.Element => {
  const { data, isLoading } = useV2ListNotificationTypesQuery();
  const { data: preferencesData, isLoading: preferencesLoading } =
    useV2ListNotificationPreferencesQuery({ page: { size: 100 } });
  const [createPreference] = useV2CreateNotificationPreferenceMutation();
  const [updatePreference] = useV2UpdateNotificationPreferenceMutation();

  const {
    valueFor,
    handleToggle,
    showFailAlert,
    setShowFailAlert,
    showSuccessAlert,
    setShowSuccessAlert,
    errorMessage,
  } = useNotificationChannelOverrides({
    resourceType: "notification-preferences",
    listData: preferencesData,
    createMutation: createPreference,
    updateMutation: updatePreference,
  });

  if (isLoading || preferencesLoading || !data) {
    return <ScopeStackSpinner />;
  }

  const sortedCategories = [...(data.categories ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );

  return (
    <Card className="whiteBg notifications">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        "Notification preferences updated",
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <Card.Header className="headerOverride">Notifications</Card.Header>
      <hr className="cardHeadDivider" style={{ marginBottom: "5px" }} />
      {sortedCategories.map((category) => {
        const typesForCategory = (data.types ?? []).filter(
          (type) => type.category === category.key
        );

        if (!typesForCategory.length) return null;

        return (
          <div key={category.key}>
            <Table className="whiteBg">
              <thead>
                <tr>
                  <th style={{ width: "70%", textAlign: "start" }}>
                    <div className="notificationCategory">{category.label}</div>
                  </th>
                  <th style={{ width: "15%", textAlign: "center" }}>In App</th>
                  <th style={{ width: "15%", textAlign: "center" }}>Email</th>
                </tr>
              </thead>
              <tbody>
                {typesForCategory.map((type) => {
                  const inAppFallback =
                    type.account_channels?.in_app ??
                    type.default_channels?.in_app ??
                    false;
                  const emailFallback =
                    type.account_channels?.email ??
                    type.default_channels?.email ??
                    false;
                  const inAppValue = valueFor(
                    type.key,
                    "in_app",
                    inAppFallback
                  );
                  const emailValue = valueFor(type.key, "email", emailFallback);

                  return (
                    <tr key={type.key}>
                      <td>
                        <div style={{ color: "#1c2655", fontSize: "15px" }}>
                          {type.name}
                        </div>
                        <div>{type.description}</div>
                      </td>
                      <td>
                        <GenericToggle
                          key={`${type.key}-in_app-${inAppValue}`}
                          initialValueOfIsOn={inAppValue}
                          onToggle={(value) =>
                            type.key && handleToggle(type.key, "in_app", value)
                          }
                          onText="On"
                          offText="Off"
                          hasText={false}
                        />
                      </td>
                      <td>
                        <GenericToggle
                          key={`${type.key}-email-${emailValue}`}
                          initialValueOfIsOn={emailValue}
                          onToggle={(value) =>
                            type.key && handleToggle(type.key, "email", value)
                          }
                          onText="On"
                          offText="Off"
                          hasText={false}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        );
      })}
    </Card>
  );
};

export default UserNotifications;
